/**
 * KPI display helpers (design-review #2, #65, #81).
 * Presentation-only; backend supplies canonical 0-100 percentages.
 */

export const KPI_TREND_PERCENT_CAP = 999;
/** Hide utilization trend badges when variance exceeds this (percentage points). */
export const KPI_UTILIZATION_VARIANCE_PP_MAX = 100;

/**
 * Parse utilization-like values from API (number or "26.8%" string).
 *
 * @param {*} value - Raw API value
 * @returns {number|null} Finite number or null
 */
export const parseUtilizationValue = (value) => {
    if (value === null || value === undefined) {
        return null;
    }

    if (typeof value === 'string') {
        const cleaned = value.trim().replace('%', '');
        if (!cleaned) {
            return null;
        }
        const parsed = Number(cleaned);
        return Number.isFinite(parsed) ? parsed : null;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Normalize ratio (0-1) or percent (0-100) to display percent 0-100.
 *
 * @param {*} value - Raw value
 * @returns {number|null}
 */
export const normalizePercentageForDisplay = (value) => {
    const parsed = parseUtilizationValue(value);
    if (parsed === null) {
        return null;
    }
    if (Math.abs(parsed) <= 1) {
        return parsed * 100;
    }
    return parsed;
};

/**
 * Cap absolute percent for KPI trend badges (design-review #2).
 *
 * @param {number|null|undefined} value - Percent magnitude
 * @returns {number|null} Capped finite value or null if not displayable
 */
export const capPercentDisplay = (value) => {
    const parsed = parseUtilizationValue(value);
    if (parsed === null) {
        return null;
    }
    const magnitude = Math.abs(parsed);
    const sign = parsed === 0 ? 0 : parsed > 0 ? 1 : -1;
    const cappedMagnitude = Math.min(magnitude, KPI_TREND_PERCENT_CAP);
    return sign * cappedMagnitude;
};

/**
 * Build trend display for KPI cards.
 *
 * @param {object} options
 * @param {number|null|undefined} options.variancePercent - Variance vs target (pp)
 * @param {string} [options.label='vs target'] - Trend label
 * @returns {{ hidden: boolean, value: string, label: string, direction: 'up'|'down'|'flat' }}
 */
export const KPI_TREND_VARIANCE_HIDE_THRESHOLD = 100;

/**
 * Whether a utilization variance (pp vs target) is safe to show as a trend badge.
 *
 * @param {number|null|undefined} variancePercent - Variance in percentage points
 * @returns {boolean}
 */
export const isDisplayableUtilizationVariance = (variancePercent) => {
    const parsed = parseUtilizationValue(variancePercent);
    if (parsed === null) {
        return false;
    }
    return Math.abs(parsed) < KPI_TREND_PERCENT_CAP
        && Math.abs(parsed) <= KPI_TREND_VARIANCE_HIDE_THRESHOLD;
};

export const formatKpiTrendDisplay = ({
    variancePercent,
    label = 'pp vs target',
}) => {
    const parsed = parseUtilizationValue(variancePercent);
    if (parsed === null || Math.abs(parsed) >= KPI_TREND_PERCENT_CAP) {
        return { hidden: true, value: '', label, direction: 'flat' };
    }

    const direction = parsed > 0 ? 'up' : parsed < 0 ? 'down' : 'flat';
    const sign = parsed > 0 ? '+' : parsed < 0 ? '-' : '';
    return {
        hidden: false,
        value: `${sign}${Math.abs(parsed).toFixed(1)}%`,
        label,
        direction,
    };
};

/**
 * Utilization status copy (design-review #25, #65).
 *
 * @param {number|null} actual - Actual utilization %
 * @param {number|null} target - Target utilization %
 * @returns {string}
 */
export const formatUtilizationVsTarget = (actual, target) => {
    const a = normalizePercentageForDisplay(actual);
    const t = normalizePercentageForDisplay(target);
    if (a === null) {
        return 'Utilization unavailable';
    }
    if (t === null) {
        return `${a.toFixed(1)}% utilization`;
    }
    if (a < t) {
        return `Below target (${a.toFixed(1)}% / ${t.toFixed(1)}%)`;
    }
    if (a > t) {
        return `Above target (${a.toFixed(1)}% / ${t.toFixed(1)}%)`;
    }
    return `On target (${a.toFixed(1)}%)`;
};

/**
 * Metric availability for empty / N/A / zero states (design-review #81).
 *
 * @param {object} options
 * @param {boolean} [options.loading]
 * @param {*} [options.value]
 * @param {boolean} [options.configured=true]
 * @returns {'loading'|'unavailable'|'zero'|'ok'}
 */
export const metricAvailabilityState = ({
    loading = false,
    value = null,
    configured = true,
}) => {
    if (loading) {
        return 'loading';
    }
    if (!configured) {
        return 'unavailable';
    }
    if (value === null || value === undefined || value === 'N/A' || value === '—') {
        return 'unavailable';
    }

    // Extract the first numeric token from formatted metric strings like:
    // - "0.0%" (utilization)
    // - "$0" (currency)
    // - "—" (handled above as unavailable)
    const numeric = (() => {
        if (typeof value === 'number') {
            return Number.isFinite(value) ? value : null;
        }
        if (typeof value !== 'string') {
            return null;
        }

        const cleaned = value.trim();
        if (!cleaned) {
            return null;
        }

        const match = cleaned.replace(/,/g, '').match(/-?\d+(\.\d+)?/);
        if (!match) {
            return null;
        }

        const parsed = Number(match[0]);
        return Number.isFinite(parsed) ? parsed : null;
    })();

    if (numeric !== null && Number.isFinite(numeric) && numeric === 0) {
        return 'zero';
    }
    return 'ok';
};
